import { SourceType } from 'module/api/filter/types';
import { Team } from 'module/api/team/types';
import { UserStatus, User as UserT, UserType } from 'module/api/user/types';
import { FieldBase, FieldType, FilterContext } from './field';
import type {
  FieldOption as FieldOptionT,
  FieldOptionBase,
  Field,
} from 'module/api/field/types';

export type FieldOption = Omit<FieldOptionT<number>, 'group'> & {
  group?: { label: string; value: number };
  value?: number;
};
// interface MetaData
//   extends Omit<FieldMetaData, 'options' | 'ratingScale' | 'visibleOnInvoice'> {
//   options?: FieldOption[];
//   tags?: FieldOption[];
//   ratingScale?: { label: string; value: RatingFieldScale };
//   visibleOnInvoice?: boolean;
// }

// TODO: combine this and the above enum, doing this write right now coz there are lot of switch case handlings
export enum KeyEventsAndIntervalFieldTypes {
  KeyEvents = 'KEY_EVENTS',
  Intervals = 'INTERVALS',
  AnalyzeIntervals = 'ANALAYSE_INTERVALS',
}

export enum FieldDataType {
  Text = 'TEXT',
  MultiLineText = 'MULTI_LINE_TEXT',
  Number = 'NUMBER',
  Boolean = 'BOOLEAN',
  Date = 'DATE',
  User = 'USER',
  Note = 'NOTE',
}

export enum FieldTypes {
  Text = 'TEXT',
  MultiLineText = 'MULTI_LINE_TEXT',
  Number = 'NUMBER',
  Boolean = 'BOOLEAN',
  Date = 'DATE',
  SingleSelect = 'SINGLE_SELECT',
  MultiSelect = 'MULTI_SELECT',
  SingleUserSelect = 'SINGLE_USER_SELECT',
  MultiUserSelect = 'MULTI_USER_SELECT',
  Rating = 'RATING',
  Note = 'NOTE',
  Level = 'LEVEL',
}

export type FieldTypeOption = {
  label: string;
  value: FieldTypes;
  description?: string | React.ReactNode;
  private?: boolean;
  sectionType?: string;
};

export const FIELD_ID_ASSIGNEE = -127;

export interface BooleanField extends FieldBase<boolean> {
  type: FieldType.Boolean;
}

export interface NumberField extends FieldBase<number> {
  type: FieldType.Number;
  operation?: string;
  metaData?: {
    required: boolean;
    default?: number;
    range?: {
      min?: number;
      max?: number;
    };
    options?: FieldOptionBase<number>[];
    rangeAvailable: boolean;
    allowAdminsAndOwnersToEdit?: boolean;
    captureKeyIntervalForFirstActivityOnly?: boolean;
    roleIds?: number[];
  };
}

export interface EstimateField extends FieldBase<number> {
  type: FieldType.Estimate;
  metaData?: {
    required: boolean;
    default?: number;
    options?: FieldOptionBase<number>[];
    allowAdminsAndOwnersToEdit?: boolean;
    captureKeyIntervalForFirstActivityOnly?: boolean;
  };
}

export interface TextField extends FieldBase<string> {
  type: FieldType.Text;
}

export interface MultiLineTextField extends FieldBase<string> {
  type: FieldType.MultiLineText;
}

export interface DateField<T extends number | string = string>
  extends FieldBase<T> {
  type: FieldType.Date;
  hideFutureDates?: boolean;
  metaData?: {
    required?: boolean;
    allowAdminsAndOwnersToEdit?: boolean;
    options?: FieldOptionBase<number>[];
    captureKeyIntervalForFirstActivityOnly?: boolean;
    /**
     * This is just a FE property
     */
    allowRelativeDateOps?: boolean;
    /**
     * This is just a FE property
     */
    allowAbsDateOps?: boolean;
    /**
     * This is just a FE property
     */
    allowBaseOps?: boolean;
    /**
     * This is just a FE property
     */
    showAllOps?: boolean;
    /**
     * This is just a FE property
     */
    filterContext?: FilterContext;
  };
}

export interface RatingField<T extends number | string = string>
  extends FieldBase<T> {
  type: FieldType.Number;
  fieldType: FieldTypes.Rating;
  hideFutureDates?: boolean;
  metaData?: {
    required?: boolean;
    ratingScale?: number;
    allowAdminsAndOwnersToEdit?: boolean;
    captureKeyIntervalForFirstActivityOnly?: boolean;
  };
}

export interface ProjectUsers {
  in_project_user_ids: number[];
  not_in_project_user_ids: number[];
}
export interface ProjectUsersByAccess {
  private: ProjectUsers;
  shared: ProjectUsers;
}

export interface UsersMetadata {
  by_user_id: {
    [userId: number]: UserT;
  };
  user_ids: number[];
}
export interface UserOption extends FieldOptionBase<number> {
  imageUrl?: string;
  description?: string;
  logoUrl?: string;
  emailId?: string;
  extra?: {
    id: number;
    type: string;
    roleId?: number;
    userType?: UserType;
    userStatus?: UserStatus;
  };
}

export interface UserFieldMetadata {
  required?: boolean;
  users: UsersMetadata;
  options?: UserOption[];
  allowAdminsAndOwnersToEdit?: boolean;
  captureKeyIntervalForFirstActivityOnly?: boolean;
}

export interface SingleUserField extends FieldBase<number> {
  type: FieldType.SingleUser;
  metaData: UserFieldMetadata;
}

export interface MultiUserField extends FieldBase<number[]> {
  type: FieldType.MultiUser;
  metaData: UserFieldMetadata;
}

export interface SingleSelectField extends FieldBase<number | string> {
  type: FieldType.SingleSelect;
  metaData: {
    required?: boolean;
    options: FieldOptionBase[];
    default?: number;
    allowAdminsAndOwnersToEdit?: boolean;
    captureKeyIntervalForFirstActivityOnly?: boolean;
    roleIds?: number[];
  };
}

export interface MultiSelectField extends FieldBase<number[]> {
  type: FieldType.MultiSelect;
  metaData: {
    required?: boolean;
    options: FieldOptionBase[];
    allowAdminsAndOwnersToEdit?: boolean;
    captureKeyIntervalForFirstActivityOnly?: boolean;
    roleIds?: number[];
  };
}

export interface NoteField extends FieldBase<string> {
  type: FieldType.Note;
  metaData: {
    required?: boolean;
    tags: FieldOptionBase[];
    options?: FieldOptionBase[];
    allowAdminsAndOwnersToEdit?: boolean;
    captureKeyIntervalForFirstActivityOnly?: boolean;
  };
}

export type UpdatableSquad = {
  users: Pick<UserT, 'userId'>[];
  teams: Pick<Team, 'teamId'>[];
};

export type UserId = ['user', number, UserT | undefined];
export const UserId = (id: number, user?: UserT): UserId =>
  (user ? ['user', id, user] : ['user', id]) as UserId;

export type TeamId = ['team', number];
export const TeamId = (id: number): TeamId => ['team', id];

export type AssigneeValue = UserId | TeamId;
export const AssigneeValue = {
  toString: (value: AssigneeValue): string => value.join('_'),
  fromString: (value: string): AssigneeValue => {
    const [type, id] = value.split('_');

    return [type, parseInt(id, 10)] as AssigneeValue;
  },
  isSame: (a: AssigneeValue, b: AssigneeValue): boolean =>
    a[0] === b[0] && a[1] === b[1],
  toSquad: (values: AssigneeValue[]): UpdatableSquad => ({
    users: values
      .filter(([type]) => type === 'user')
      .map(([, userId]) => ({ userId })),
    teams: values
      .filter(([type]) => type === 'team')
      .map(([, teamId]) => ({ teamId })),
  }),
};

export interface AssigneeField extends FieldBase<AssigneeValue[]> {
  type: FieldType.Assignee;
  fieldGroup: string;
  metaData: UserFieldMetadata & {
    teams: Team[];
    projectPlaceholders?: { [id: number]: Team[] };
  };
}

export const AssigneeField = (
  users: UsersMetadata,
  teams: Team[],
): AssigneeField => ({
  fieldId: FIELD_ID_ASSIGNEE,
  private: false,
  type: FieldType.Assignee,
  customField: false,
  fieldName: 'assignee',
  metaData: { users, teams },
  fieldGroup: '_native',
  sourceType: SourceType.Task,
});

export type FilterableField =
  | BooleanField
  | NumberField
  | TextField
  | MultiLineTextField
  | DateField
  | SingleUserField
  | MultiUserField
  | SingleSelectField
  | MultiSelectField
  | AssigneeField
  | EstimateField
  | NoteField;

export type FieldLookupByName = Record<string, FilterableField>;
export type FieldLookupById = Record<number, FilterableField>;

export type FieldBases = FilterableField &
  Pick<Field, 'fieldDataType' | 'fieldType' | 'optionsAvailable' | 'isEnabled'>;
